import { AppointmentsRepository } from "@/repositories/appointments.repository";
import { FINISHED_OR_COMPLETED, finishedOrCompletedCount } from "@/repositories/appointment-records";
import { appointment_status } from "@/models/appointment.model";

export interface DashboardStats {
  totalAppointments: number;
  newAppointments: number;
  completedAppointments: number;
  pendingAppointments: number;
  inProgressAppointments: number;
}

export interface DashboardAppointment {
  id: string;
  property: {
    name: string;
    unit_number: string;
  };
  service: {
    name: string;
    parent_name: string;
  };
  date: string;
  status: appointment_status;
  created_at: string;
}

export interface DashboardData {
  stats: DashboardStats;
  upcomingAppointments: DashboardAppointment[];
  completedAppointments: DashboardAppointment[];
}

export const dashboardApi = {
  async getDashboardData(): Promise<DashboardData> {
    // Get appointment statistics
    const stats = await this.getDashboardStats();

    // Get upcoming appointments (next 7 days)
    const upcomingAppointments = await this.getUpcomingAppointments();

    // Get recently completed appointments (last 7 days)
    const completedAppointments = await this.getCompletedAppointments();

    return {
      stats,
      upcomingAppointments,
      completedAppointments,
    };
  },

  async getDashboardStats(): Promise<DashboardStats> {
    const { total, byStatus } = await AppointmentsRepository.countsForIndividual();

    return {
      totalAppointments: total,
      newAppointments: byStatus[appointment_status.assigned],
      completedAppointments: finishedOrCompletedCount(byStatus),
      pendingAppointments: byStatus[appointment_status.pending],
      inProgressAppointments: byStatus[appointment_status.inprogress],
    };
  },

  async getUpcomingAppointments(): Promise<DashboardAppointment[]> {
    const result = await AppointmentsRepository.listForIndividual(
      1,
      10,
      {
        status: appointment_status.assigned,
      },
      "date",
      "asc"
    );

    return result.data.map((apt) => ({
      id: apt.id,
      property: {
        name: apt.property?.name || "",
        unit_number: apt.property?.unit_number || "",
      },
      service: {
        name: apt.service?.name || "",
        parent_name: apt.service?.parent_name || "",
      },
      date: apt.date,
      status: apt.status,
      created_at: apt.created_at,
    }));
  },

  async getCompletedAppointments(): Promise<DashboardAppointment[]> {
    const result = await AppointmentsRepository.listForIndividual(
      1,
      10,
      {
        statuses: FINISHED_OR_COMPLETED,
      },
      "date",
      "desc"
    );

    return result.data.map((apt) => ({
      id: apt.id,
      property: {
        name: apt.property?.name || "",
        unit_number: apt.property?.unit_number || "",
      },
      service: {
        name: apt.service?.name || "",
        parent_name: apt.service?.parent_name || "",
      },
      date: apt.date,
      status: apt.status,
      created_at: apt.created_at,
    }));
  },
};
